/**
 * hero-slider.js — behaviour for sections/hero-slider.liquid.
 *
 * Ported from the Next.js storefront's src/components/hero-slider.tsx. The
 * markup is rendered by Liquid and the first slide is already visible before
 * this file runs, so everything here is progressive enhancement: with JS off
 * the hero is a static first slide with a working link and CTA.
 *
 * Implemented as a custom element so it survives the theme editor re-rendering
 * a section (connectedCallback runs again on the new DOM) without any
 * shopify:section:load bookkeeping.
 *
 * Behaviours carried over from the React component:
 *   - autoplay on a settable interval (the source used a fixed 6000ms)
 *   - wrap-around next/prev, dot indicators
 *   - touch swipe with a 50px threshold; a left swipe advances
 *   - crossfade between slides (the CSS owns the transition)
 * Behaviours added here because a Liquid theme has to be keyboard- and
 * editor-friendly:
 *   - pause on hover, on focus-within, and while the tab is hidden
 *   - left/right arrow keys move between slides
 *   - autoplay is skipped entirely under prefers-reduced-motion
 *   - shopify:block:select jumps to (and holds) the slide a merchant clicked
 *
 * The mobile "natural height" trick is the source's, and its reasoning is
 * worth repeating: staff often skip the dedicated mobile image, so a wide
 * desktop image ends up on phones. Rather than crop it into a fixed-height box
 * (which lops off the edges — and any text baked into the artwork), a slide
 * with no text overlay publishes its own aspect ratio and the section adopts
 * it on mobile, so the whole picture is visible. Slides that DO carry a text
 * overlay keep the fixed-height box, since there the image is only a backdrop.
 */
(function () {
  if (window.customElements.get('hero-slider')) return;

  /** Matches the React source's useSwipe() threshold. */
  var SWIPE_THRESHOLD = 50;

  class HeroSlider extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.timer = null;
      this.hovered = false;
      this.focused = false;
      this.editorHold = false;
      this.touchStartX = null;

      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
    }

    connectedCallback() {
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-hero-dot]'));
      if (this.slides.length === 0) return;

      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled =
        this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reduceMotion;

      var previous = this.querySelector('[data-hero-prev]');
      var next = this.querySelector('[data-hero-next]');
      if (previous) previous.addEventListener('click', this.previous.bind(this));
      if (next) next.addEventListener('click', this.next.bind(this));

      this.dots.forEach(
        function (dot) {
          dot.addEventListener(
            'click',
            function () {
              this.goTo(parseInt(dot.dataset.index, 10) || 0);
            }.bind(this)
          );
        }.bind(this)
      );

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });

      this.addEventListener(
        'mouseenter',
        function () {
          this.hovered = true;
          this.stop();
        }.bind(this)
      );
      this.addEventListener(
        'mouseleave',
        function () {
          this.hovered = false;
          this.start();
        }.bind(this)
      );
      this.addEventListener(
        'focusin',
        function () {
          this.focused = true;
          this.stop();
        }.bind(this)
      );
      this.addEventListener(
        'focusout',
        function () {
          this.focused = false;
          this.start();
        }.bind(this)
      );

      document.addEventListener('visibilitychange', this.handleVisibility);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

      // Re-assert the initial state so the mobile aspect ratio and the ARIA
      // flags agree with the markup even if a slide was removed server-side.
      this.render();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.handleVisibility);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    /* ---------------------------------------------------------------- state */

    goTo(index) {
      if (!this.slides || this.slides.length === 0) return;
      var count = this.slides.length;
      this.index = ((index % count) + count) % count;
      this.render();
      this.restart();
    }

    next() {
      this.goTo(this.index + 1);
    }

    previous() {
      this.goTo(this.index - 1);
    }

    render() {
      var active = this.slides[this.index];

      this.slides.forEach(
        function (slide, i) {
          var isActive = i === this.index;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          // `inert` keeps the offscreen slides' links and buttons out of the
          // tab order and out of reach of a stray click on the invisible layer.
          if (isActive) {
            slide.removeAttribute('inert');
          } else {
            slide.setAttribute('inert', '');
          }
        }.bind(this)
      );

      this.dots.forEach(
        function (dot, i) {
          var isActive = i === this.index;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        }.bind(this)
      );

      // Mobile natural height — see the file header for why this exists.
      var ratio = active ? active.dataset.mobileRatio : '';
      if (ratio) {
        this.style.setProperty('--hero-mobile-ratio', ratio);
        this.setAttribute('data-mobile-natural', '');
      } else {
        this.style.removeProperty('--hero-mobile-ratio');
        this.removeAttribute('data-mobile-natural');
      }
    }

    /* -------------------------------------------------------------- autoplay */

    start() {
      if (!this.autoplayEnabled) return;
      if (this.hovered || this.focused || this.editorHold || document.hidden) return;
      if (this.timer) return;
      this.timer = window.setInterval(this.next.bind(this), this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    handleVisibility() {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    }

    /* ---------------------------------------------------------------- input */

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    handleTouchStart(event) {
      this.touchStartX = event.touches[0].clientX;
    }

    handleTouchEnd(event) {
      if (this.touchStartX === null) return;
      var difference = event.changedTouches[0].clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(difference) < SWIPE_THRESHOLD) return;
      if (difference < 0) {
        this.next();
      } else {
        this.previous();
      }
    }

    /* --------------------------------------------------------- theme editor */

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      this.editorHold = true;
      this.stop();
      var index = this.slides.indexOf(event.target);
      if (index > -1) {
        this.index = index;
        this.render();
      }
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) return;
      this.editorHold = false;
      this.start();
    }
  }

  window.customElements.define('hero-slider', HeroSlider);
})();
